import {
  Button,
  Circle,
  HStack,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MessageThreadFragment } from "gql/graphql";
import { VideoCallIcon } from "modules/icons";
import { useAuth } from "modules/providers/auth-provider";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { memo, useCallback } from "react";
import colors from "app/colors";
import { TextAlignment } from "modules/utils/Constants";
import CircularIconButton from "./circular-icon-button";

interface IProps {
  messageThread?: MessageThreadFragment;
  userIds?: string[];
  circular?: boolean;
  hasText?: boolean;
  textAlignment?: TextAlignment;
  iconOnly?: boolean;
  circularButtonSize?: string | number;
  circularIconSize?: string | number;
}

const VideoChatButton = ({
  messageThread,
  userIds,
  circular,
  hasText,
  textAlignment = "bottom",
  iconOnly = false,
  circularButtonSize = 8,
  circularIconSize = 6,
}: IProps) => {
  const { videoChat: videoChatFeatureFlag } = useFlags();
  const { me } = useAuth();
  const { openModal } = useOverlay();

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      const ids =
        (messageThread?.accessUserIdList ?? userIds)?.filter(
          (userId) => userId !== me?.id,
        ) ?? [];
      openModal({
        type: MODAL_TYPE.CALL_OPTIONS,
        ids,
      });
    },
    [messageThread?.accessUserIdList, userIds, me?.id, openModal],
  );

  if (messageThread?.meRemovedUser || !videoChatFeatureFlag) return null;

  const iconFill = iconOnly ? colors.blue : colors.white;
  const icon = (
    <VideoCallIcon boxSize={circular ? circularIconSize : 6} fill={iconFill} />
  );
  const ariaLabel = "Video Call";
  const buttonBackground = iconOnly ? colors.transparent : colors.blue;
  const buttonHoverBackground = iconOnly ? colors.transparent : colors.blue;

  if (hasText && textAlignment === "inline") {
    return (
      <Button
        onClick={onClick}
        aria-label={ariaLabel}
        variant="ghost"
        height="auto"
        px={0}
      >
        <HStack spacing={1}>
          <Circle bg={colors.gray40} size={6}>
            <VideoCallIcon boxSize={4} fill={colors.white} />
          </Circle>
          <Text
            fontSize={10}
            fontWeight={500}
            color={colors.gray50}
            textAlign="center"
          >
            Video Call
          </Text>
        </HStack>
      </Button>
    );
  }

  return (
    <VStack spacing={1}>
      {circular ? (
        <CircularIconButton
          icon={icon}
          aria-label={ariaLabel}
          boxSize={circularButtonSize}
          minW={circularButtonSize}
          h={circularButtonSize}
          p={0}
          backgroundColor={colors.gray40}
          onClick={onClick}
        />
      ) : (
        <IconButton
          icon={icon}
          aria-label={ariaLabel}
          size="xs"
          w={iconOnly ? "24px" : "32px"}
          minW={iconOnly ? "24px" : undefined}
          h={iconOnly ? "24px" : undefined}
          p={iconOnly ? 0 : undefined}
          variant={iconOnly ? "ghost" : undefined}
          borderRadius="4px"
          backgroundColor={buttonBackground}
          _hover={{
            backgroundColor: buttonHoverBackground,
          }}
          _active={{
            backgroundColor: buttonHoverBackground,
          }}
          onClick={onClick}
        />
      )}
      {hasText && (
        <Text
          fontSize={10}
          fontWeight={500}
          color={colors.gray50}
          textAlign="center"
          lineHeight="10px"
        >
          Video
          <br />
          Call
        </Text>
      )}
    </VStack>
  );
};

export default memo(VideoChatButton);
